import "@xyflow/react/dist/style.css";

import { Box, Loader } from "@saviynt/design-system";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { AccountsMap } from "./AccountsMap";
import { GraphActions } from "./components";
import { CriticalHighRisk, Search } from "./components/SidePanel";
import { type AccessMapSuccessResponse, type NodeData } from "./types";
import AssetTimelineHeader from "./AssetTimelineHeader";
import { useAccessMAP } from "../../hooks/usePolicyFindings";
import "./PoliciesAccessMap.css";
import LoadErrorWrapper from "../../components/LoadErrorWrapper/LoadErrorWrapper";

export default function IAMGraph({ assetId }: { assetId: string }) {
  const [id, setId] = useState(assetId);
  const intl = useIntl();

  const [accessMapData, setAccessMapData] =
    useState<AccessMapSuccessResponse>();
  const [previousNodes, setPreviousNodes] = useState<NodeData[]>(
    [] as NodeData[],
  );
  const { data, error, isLoading } = useAccessMAP(id);

  useEffect(() => {
    if (!isLoading && data) {
      setAccessMapData(data);
    }
  }, [data, isLoading]);

  useEffect(() => {
    document.body.classList.add("Ispm-body-dark-mode-fix");
    window.scrollTo(0, 0); // Reset scroll position to the top

    return () => {
      document.body.classList.remove("Ispm-body-dark-mode-fix");
    };
  }, []);

  const onPreviousNodeClick = useCallback(() => {
    const lastNode = previousNodes[previousNodes.length - 1];

    setPreviousNodes(() => previousNodes.slice(0, previousNodes.length - 1));

    setId(lastNode.id);
  }, [previousNodes]);

  const onMakeItFocusedNodeClick = useCallback(
    (node: NodeData) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (data?.data.nodes) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const root = data.data.nodes.find((n) => n.id === id)!;

        setPreviousNodes((prevNodes) => [...prevNodes, { ...root }]);
      }

      setId(node.id);
    },
    [data, id],
  );

  const [isSearchSidePanelOpen, setIsSearchSidePanelOpen] = useState(false);
  const [isCriticalHighRiskSidePanelOpen, setIsCriticalHighRiskSidePanelOpen] =
    useState(false);

  if (isLoading) {
    return (
      <div className="NHI-AccessMap-Loader">
        <Loader kind="loop" format="main" color="brand" size="large" />
      </div>
    );
  }

  let isError = false;

  if (
    error ||
    !accessMapData ||
    !accessMapData.data ||
    !accessMapData.data.nodes ||
    accessMapData.data.nodes.length === 0
  ) {
    isError = true;
  }

  return (
    <Box
      className="w-full"
      ariaLabel={intl.formatMessage({
        defaultMessage: "Access Map for Non-Human Identities",
        description: "Access Map for Non-Human Identities",
        id: "TujaSJ",
      })}
    >
      <AssetTimelineHeader />
      {isError || !accessMapData ? (
        <LoadErrorWrapper isError={true} isLoading={false} />
      ) : (
        <Box
          className="AccessMap"
          ariaLabel={intl.formatMessage({
            defaultMessage:
              "Interactive access map showing relationships between identities",
            description:
              "Interactive access map showing relationships between identities",
            id: "pwJ6IY",
          })}
        >
          <AccountsMap
            id={id}
            data={accessMapData}
            previousNodes={previousNodes}
            onPreviousNodeClick={onPreviousNodeClick}
            onMakeItFocusedNodeClick={onMakeItFocusedNodeClick}
            closeSearchSidePanel={() => setIsSearchSidePanelOpen(false)}
            closeCriticalHighRiskSidePanel={() =>
              setIsCriticalHighRiskSidePanelOpen(false)
            }
          />
          <GraphActions
            setIsSearchSidePanelOpen={setIsSearchSidePanelOpen}
            setIsCriticalHighRiskSidePanelOpen={
              setIsCriticalHighRiskSidePanelOpen
            }
            isSearchSidePanelOpen={isSearchSidePanelOpen}
            isCriticalHighRiskSidePanelOpen={isCriticalHighRiskSidePanelOpen}
            data={accessMapData}
          />
        </Box>
      )}
      <Search
        data={data}
        isOpen={isSearchSidePanelOpen}
        setIsOpen={setIsSearchSidePanelOpen}
      />
      <CriticalHighRisk
        data={data}
        isOpen={isCriticalHighRiskSidePanelOpen}
        setIsOpen={setIsCriticalHighRiskSidePanelOpen}
      />
    </Box>
  );
}
